import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {finalize} from 'rxjs';
import {MaterialModule} from '../../../material.module';
import {ConnectIntegrationsService, IntegrationSummary} from '../../../services/connect/connect-integrations.service';
import {ConnectIntegrationsComponent} from './connect-integrations.component';
import {ScheduledJobHealthComponent} from './scheduled-job-health.component';
import {CodedIntegrationShellComponent} from './coded-integration-shell.component';
import {CODED_INTEGRATION_VIEWS} from './generated-coded-views';
import {ConnectRepositoriesComponent} from './connect-repositories.component';

@Component({
    selector: 'app-connect-installations', standalone: true,
    imports: [CommonModule, MaterialModule, ConnectIntegrationsComponent, ScheduledJobHealthComponent, CodedIntegrationShellComponent, ConnectRepositoriesComponent],
    templateUrl: './connect-installations.component.html',
    styleUrls: ['./connect-installations.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConnectInstallationsComponent implements OnInit {
    private readonly api = inject(ConnectIntegrationsService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private pendingOpenId: string | null = null;
    readonly local = this.route.snapshot.data['localIntegration'] === true;
    readonly installations = signal<IntegrationSummary[]>([]);
    readonly selected = signal<IntegrationSummary | null>(null);
    readonly loading = signal(false);
    readonly error = signal<string | null>(null);
    readonly search = signal('');
    readonly repositoriesOpen = signal(false);
    readonly installationAvailable = signal(false);
    readonly requestedId = signal<string | null>(null);
    readonly invalidSelection = signal(false);
    readonly detailRequested = computed(() => this.requestedId() !== null || this.invalidSelection());
    readonly filteredInstallations = computed(() => {
        const query = this.search().trim().toLocaleLowerCase();
        return this.installations().filter(item => [item.name, item.technicalKey, item.application?.packageVersion ?? '']
            .some(value => value.toLocaleLowerCase().includes(query)));
    });
    readonly columns = ['name', 'version', 'status', 'runtime', 'actions'];

    ngOnInit(): void {
        this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
            const wasDetail = this.detailRequested();
            this.pendingOpenId = null;
            const values = params.getAll('integration');
            const valid = values.length === 1 && /^[a-z][a-z0-9-]{2,63}$/.test(values[0]);
            this.requestedId.set(valid ? values[0] : null);
            this.invalidSelection.set(values.length > 0 && !valid);
            this.resolveSelection();
            if (wasDetail && !this.detailRequested()) this.refresh();
        });
        this.refresh();
    }

    refresh(openAfterRefresh?: string): void {
        if (openAfterRefresh) this.pendingOpenId = openAfterRefresh;
        if (this.loading()) return;
        this.installationAvailable.set(false); this.repositoriesOpen.set(false);
        this.loading.set(true); this.error.set(null);
        this.api.list().pipe(finalize(() => this.loading.set(false)), takeUntilDestroyed(this.destroyRef)).subscribe({
            next: result => {
                const ids = new Set<string>();
                if (!Array.isArray(result.items) || result.items.length > 20 || result.items.some(item => {
                    if (!item || typeof item.id !== 'string' || !/^[a-z][a-z0-9-]{2,63}$/.test(item.id) || ids.has(item.id)
                        || typeof item.name !== 'string' || typeof item.technicalKey !== 'string'
                        || item.application?.packageVersion !== undefined && typeof item.application.packageVersion !== 'string') return true;
                    ids.add(item.id); return false;
                })) {
                    this.pendingOpenId = null;
                    this.installations.set([]); this.selected.set(null); this.error.set('The integration list could not be verified. Refresh to try again.'); return;
                }
                const items = result.items.filter(item => item.capabilities?.read === true)
                    .sort((first, second) => first.name.localeCompare(second.name));
                this.installationAvailable.set(result.capabilities?.installationAvailable === true);
                this.installations.set(items);
                this.resolveSelection();
                const pendingOpenId = this.pendingOpenId;
                this.pendingOpenId = null;
                if (pendingOpenId) this.select(pendingOpenId);
            },
            error: () => { this.pendingOpenId = null; this.installations.set([]); this.selected.set(null); this.error.set('Integrations could not be loaded. Refresh your access and try again.'); },
        });
    }
    select(id: string): void {
        this.pendingOpenId = null;
        const selected = this.installations().find(item => item.id === id);
        if (!selected) {
            this.selected.set(null); this.invalidSelection.set(true); return;
        }
        this.repositoriesOpen.set(false);
        void this.router.navigate([], {relativeTo: this.route, queryParams: {integration: id}, queryParamsHandling: 'merge'});
    }
    selectInstalled(id: string): void {
        if (this.installations().some(item => item.id === id)) this.select(id);
        else this.refresh(id);
    }
    backToList(): void {
        this.pendingOpenId = null;
        this.repositoriesOpen.set(false);
        this.requestedId.set(null); this.invalidSelection.set(false); this.selected.set(null);
        void this.router.navigate([], {relativeTo: this.route, queryParams: {integration: null}, queryParamsHandling: 'merge'});
        this.refresh();
    }
    status(item: IntegrationSummary): string {
        if (item.running) return 'Running';
        if (item.application?.paused) return 'Paused';
        if (item.application?.continuous?.status === 'needs_attention' || item.application?.reconciliationRequired
            || item.application?.latestExecution?.status === 'failed') return 'Needs attention';
        return item.activeRevision ? 'Enabled' : 'Needs setup';
    }
    runtime(item: IntegrationSummary): string {
        return item.runtimeAvailable === true ? 'Available' : item.runtimeAvailable === false ? 'Asleep or unavailable' : 'Status unavailable';
    }
    private resolveSelection(): void {
        this.selected.set(this.invalidSelection() ? null : this.installations().find(item => item.id === this.requestedId()) ?? null);
    }
    view(item: IntegrationSummary): 'synthetic' | 'health' | 'coded' | null {
        if (item.capabilities?.read !== true) return null;
        if (item.uiKey === 'synthetic-transfer' && (item.runtimeProfile === 'synthetic-transfer.v1' || this.local && item.runtimeProfile === undefined)) return 'synthetic';
        if (item.uiKey === 'scheduled-job-health' && item.runtimeProfile === 'scheduled-job-health.v1') return 'health';
        if (item.runtimeProfile === 'coded-integration.v1' && (item.uiKind === 'shine.sandboxed-ui.v1' || Object.hasOwn(CODED_INTEGRATION_VIEWS, item.uiKey))) return 'coded';
        return null;
    }
}
